import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import Send from '@mui/icons-material/Send';
import { Box, Typography, InputLabel, Stack, Grid, Switch, styled } from '@mui/material';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { inviteUser, IInviteStatus } from 'wisestep-api';
import {
  TagsInput,
  WsButton,
  WsColors,
  WsTypography,
  WsTypoColors,
  WsTypoVariants,
  WsSelect,
  Notification,
  WsSelectObj,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../context';
import { inviteDefaultValues } from '../helper/defaultValues';
import { inviteSchema } from '../helper/validation';
import InvitedUserType from '../types';

import CurrentInvitesList from './CurrentInvitesList';

const InviteFormHeader = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  backgroundColor: WsColors.TertiaryShades50,
  borderRadius: 'inherit',
}));

type IInviteFormProps = {
  onSuccess?: () => void;
  handleClose: () => void;
  defaultInvites?: string[];
  pendingUsersLimit?: number;
  rolesFromSettings?: string[];
};

const InviteForm = ({
  onSuccess,
  handleClose,
  defaultInvites = [],
  pendingUsersLimit,
  rolesFromSettings,
}: IInviteFormProps) => {
  const workspace = useWorkspaceContext();

  const [inviteUserSuccess, setInviteUserSuccess] = useState<boolean>(false);

  const methods = useForm({
    resolver: yupResolver(inviteSchema),
    defaultValues: {
      ...inviteDefaultValues,
      invitedUsers: defaultInvites,
      role: 'User',
    },
  });
  const [usersList, setUsersList] = useState<InvitedUserType[]>([]);

  const roles: WsSelectObj[] | undefined = useMemo(
    () => rolesFromSettings?.map((role) => ({ value: role, label: role } as WsSelectObj)),
    [rolesFromSettings]
  );

  const addInvites = async (data) => {
    data.invitedUsers.map((invitedEmail) => {
      setUsersList((prev) => [
        ...prev,
        {
          email: invitedEmail,
          role: data.role,
          downloadAccess: data.downloadAccess,
          status: IInviteStatus.INVITED,
        },
      ]);
    });

    await inviteUser({
      emails: data.invitedUsers,
      role: data.role,
      roles: data.downloadAccess ? ['ROLE_USER', 'ROLE_DOWNLOAD'] : ['ROLE_USER'],
      workspaceId: workspace.id || '',
    });
    setInviteUserSuccess(true);
    onSuccess && onSuccess();
    handleClose();
  };

  return (
    <FormProvider {...methods}>
      <Box bgcolor={WsColors.White} borderRadius={1.5}>
        <InviteFormHeader>
          <WsTypography>Invite Users</WsTypography>
          <Stack direction={'row'} alignItems={'center'}>
            <Typography sx={{ px: 2, fontSize: '12px', fontWeight: 600 }}>
              {pendingUsersLimit} invites left
            </Typography>
            <CloseIcon
              sx={{ fill: WsColors.BorderGrey, cursor: 'pointer' }}
              onClick={handleClose}
            />
          </Stack>
        </InviteFormHeader>
        <Box p={2} component="form" onSubmit={methods.handleSubmit(addInvites)} width="100%">
          <InputLabel sx={{ fontSize: '14px', fontWeight: 700, color: WsColors.GenericRichBlack }}>
            Email Address
          </InputLabel>
          <Grid container display="flex" alignItems={'flex-start'} spacing={1} mt={0.5}>
            <Grid item xs={8}>
              <TagsInput
                name={'invitedUsers'}
                fullWidth
                size="small"
                initialValue={defaultInvites}
                placeholder="Ex: user@example.com"
                onTagsChange={(emails) => methods.setValue('invitedUsers', emails)}
                multiple={true}
              />
            </Grid>
            <Grid item xs={2}>
              <WsSelect name={'role'} selectArray={roles ?? []} size="small" />
            </Grid>
            <Grid item xs={2}>
              <WsButton
                size="large"
                disabled={pendingUsersLimit === 0}
                type="submit"
                startIcon={<Send />}
              >
                Send Invite
              </WsButton>
            </Grid>
          </Grid>
          <Stack alignItems="center" direction={'row'} spacing={2} my={2}>
            <InputLabel>Enable Download Access</InputLabel>
            <Switch
              defaultChecked={false}
              onChange={(ev) => methods.setValue('downloadAccess', ev.target.checked)}
            />
          </Stack>
          <WsTypography
            variant={WsTypoVariants.BODY2}
            color={WsTypoColors.PRIMARY}
          >{`${usersList.length} of ${pendingUsersLimit} invites sent`}</WsTypography>
          {usersList.length > 0 && <CurrentInvitesList usersList={usersList} />}
        </Box>
      </Box>
      {inviteUserSuccess && (
        <Notification
          vertical="top"
          horizontal="center"
          autoHideDuration={3000}
          notificationObject={{
            message: 'Users Invited Successfully!',
            type: 'success',
          }}
          notify={true}
        />
      )}
    </FormProvider>
  );
};

export default InviteForm;
